import React, { useEffect, useState } from "react";
import { Input } from "../../ui";
import {
  getMutedText,
  getPrimaryText,
  getSecondaryText,
} from "../../../utils/themeUtils";
import Tooltip from "../../common/Tooltip";
import { useTheme } from "../../../context/ThemeContext";
import { localTodayYmd, parseDateOnly, formatDateOnly } from "@mediatechgroup/calendar-date";

interface PaymentPlanCreatorProps {
  invoiceAmount: number;
  onPlanChange: (plan: PaymentPlan | null) => void;
  membershipId: string;
  hasCardOnFile: boolean;
}

export interface PaymentPlan {
  installments: number;
  frequency: "weekly" | "biweekly" | "monthly";
  firstPaymentDate: string;
  payFirstNow: boolean;
  installmentAmount: number;
  schedule: PaymentScheduleItem[];
}

export interface PaymentScheduleItem {
  installmentNumber: number;
  date: string;
  amount: number;
}

const PaymentPlanCreator: React.FC<PaymentPlanCreatorProps> = ({
  invoiceAmount,
  onPlanChange,
  membershipId,
  hasCardOnFile,
}) => {
  const { isDarkMode } = useTheme();
  const [enabled, setEnabled] = useState(false);
  const [installments, setInstallments] = useState(3);
  const [frequency, setFrequency] = useState<"weekly" | "biweekly" | "monthly">("monthly");
  const [payFirstNow, setPayFirstNow] = useState(true);
  const [firstPaymentDate, setFirstPaymentDate] = useState("");

  // Calculate installment amount
  const installmentAmount = invoiceAmount / installments;

  // Generate payment schedule
  const generateSchedule = (): PaymentScheduleItem[] => {
    const schedule: PaymentScheduleItem[] = [];
    // Local midnight of the chosen day. `new Date("2026-02-01")` is UTC
    // midnight — Jan 31 in US zones — so monthly installments rolled from
    // Jan 31 and landed on the wrong day. See @mediatechgroup/calendar-date's date-only helpers.
    const startDate = parseDateOnly(firstPaymentDate) ?? new Date();

    // If paying first now, start from next period for scheduled payments
    const scheduleStartIndex = payFirstNow ? 2 : 1;
    const scheduleCount = payFirstNow ? installments - 1 : installments;

    for (let i = 0; i < scheduleCount; i++) {
      const installmentNum = scheduleStartIndex + i;
      const date = new Date(startDate);

      // Calculate date based on frequency
      if (payFirstNow) {
        // Start from next period
        if (frequency === "weekly") {
          date.setDate(date.getDate() + (i + 1) * 7);
        } else if (frequency === "biweekly") {
          date.setDate(date.getDate() + (i + 1) * 14);
        } else {
          date.setMonth(date.getMonth() + (i + 1));
        }
      } else {
        // Start from first payment date
        if (frequency === "weekly") {
          date.setDate(date.getDate() + i * 7);
        } else if (frequency === "biweekly") {
          date.setDate(date.getDate() + i * 14);
        } else {
          date.setMonth(date.getMonth() + i);
        }
      }

      schedule.push({
        installmentNumber: installmentNum,
        date: localTodayYmd(date),
        amount: installmentAmount,
      });
    }

    return schedule;
  };

  // Update parent component when plan changes
  useEffect(() => {
    if (!enabled) {
      onPlanChange(null);
      return;
    }

    if (!firstPaymentDate) {
      const today = localTodayYmd();
      setFirstPaymentDate(today);
      return;
    }

    const plan: PaymentPlan = {
      installments,
      frequency,
      firstPaymentDate,
      payFirstNow,
      installmentAmount,
      schedule: generateSchedule(),
    };

    onPlanChange(plan);
  }, [enabled, installments, frequency, firstPaymentDate, payFirstNow, invoiceAmount]);

  // Set default first payment date
  useEffect(() => {
    if (enabled && !firstPaymentDate) {
      const today = localTodayYmd();
      setFirstPaymentDate(today);
    }
  }, [enabled]);

  if (!hasCardOnFile) {
    return (
      <div
        className={`border rounded p-4 mb-4 ${
          isDarkMode
            ? "bg-yellow-900/20 border-yellow-700"
            : "bg-yellow-50 border-yellow-200"
        }`}
      >
        <p
          className={`text-sm ${
            isDarkMode ? "text-yellow-200" : "text-yellow-800"
          }`}
        >
          ⚠ Payment plans require a saved payment method. Member must add a card
          before setting up a payment plan.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`border rounded p-4 mb-4 ${
        isDarkMode
          ? "bg-gray-700 border-gray-800"
          : "bg-gray-50 border-gray-200"
      }`}
    >
      {/* Enable Payment Plan */}
      <Tooltip content="Create a payment plan to split this invoice into multiple smaller payments over time. The first payment can be processed immediately or scheduled for a future date.">
        <label className="flex items-center gap-2 cursor-help mb-4">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span className={`font-semibold ${getPrimaryText(isDarkMode)}`}>
            Create Payment Plan
          </span>
        </label>
      </Tooltip>

      {enabled && (
        <div className="space-y-4">
          {/* Number of Installments */}
          <div>
            <Input
              label="Number of Installments"
              type="number"
              min={2}
              max={12}
              value={installments}
              onChange={(e) => setInstallments(Number(e.target.value))}
              fullWidth={false}
              wrapperClassName="w-32"
            />
            <p className={`text-xs mt-1 ${getMutedText(isDarkMode)}`}>
              Split payment into 2-12 installments
            </p>
          </div>

          {/* Payment Frequency */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${getSecondaryText(
                isDarkMode
              )}`}
            >
              Payment Frequency
            </label>
            <div className="flex flex-wrap gap-3">
              <label className={`flex items-center gap-2 ${getSecondaryText(isDarkMode)}`}>
                <input
                  type="radio"
                  name="frequency"
                  value="weekly"
                  checked={frequency === "weekly"}
                  onChange={(e) => setFrequency(e.target.value as any)}
                />
                <span>Weekly</span>
              </label>
              <label className={`flex items-center gap-2 ${getSecondaryText(isDarkMode)}`}>
                <input
                  type="radio"
                  name="frequency"
                  value="biweekly"
                  checked={frequency === "biweekly"}
                  onChange={(e) => setFrequency(e.target.value as any)}
                />
                <span>Bi-weekly</span>
              </label>
              <label className={`flex items-center gap-2 ${getSecondaryText(isDarkMode)}`}>
                <input
                  type="radio"
                  name="frequency"
                  value="monthly"
                  checked={frequency === "monthly"}
                  onChange={(e) => setFrequency(e.target.value as any)}
                />
                <span>Monthly</span>
              </label>
            </div>
          </div>

          {/* First Payment Options */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${getSecondaryText(
                isDarkMode
              )}`}
            >
              First Payment
            </label>
            <div className="flex flex-col gap-2">
              <label className={`flex items-center gap-2 ${getSecondaryText(isDarkMode)}`}>
                <input
                  type="radio"
                  name="firstPayment"
                  checked={payFirstNow}
                  onChange={() => setPayFirstNow(true)}
                />
                <span>Process first payment immediately</span>
              </label>
              <label className={`flex items-center gap-2 ${getSecondaryText(isDarkMode)}`}>
                <input
                  type="radio"
                  name="firstPayment"
                  checked={!payFirstNow}
                  onChange={() => setPayFirstNow(false)}
                />
                <span>Schedule all payments for future dates</span>
              </label>
            </div>
          </div>

          {/* First Payment Date (if scheduling all) */}
          {!payFirstNow && (
            <div>
              <Input
                label="First Payment Date"
                type="date" autoComplete="off"
                value={firstPaymentDate}
                onChange={(e) => setFirstPaymentDate(e.target.value)}
                min={localTodayYmd()}
              />
            </div>
          )}

          {/* Payment Preview */}
          <div
            className={`border-t pt-4 mt-4 ${
              isDarkMode ? "border-gray-600" : "border-gray-300"
            }`}
          >
            <p
              className={`font-semibold mb-3 ${getPrimaryText(isDarkMode)}`}
            >
              Payment Schedule Preview
            </p>

            {/* First payment if paying now */}
            {payFirstNow && (
              <div
                className={`mb-3 p-3 rounded ${
                  isDarkMode
                    ? "bg-green-900/20 border border-green-700"
                    : "bg-green-50 border border-green-200"
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <span className={`font-medium ${getSecondaryText(isDarkMode)}`}>
                      Payment 1 of {installments}
                    </span>
                    <p className={`text-xs ${getMutedText(isDarkMode)}`}>
                      Processed immediately
                    </p>
                  </div>
                  <span className={`font-bold ${getPrimaryText(isDarkMode)}`}>
                    ${installmentAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Scheduled payments */}
            <div className="space-y-2">
              {generateSchedule().map((item) => (
                <div
                  key={item.installmentNumber}
                  className={`flex justify-between items-center p-2 rounded ${
                    isDarkMode ? "bg-gray-600/50" : "bg-white"
                  }`}
                >
                  <div>
                    <span className={`font-medium ${getSecondaryText(isDarkMode)}`}>
                      Payment {item.installmentNumber} of {installments}
                    </span>
                    <p className={`text-xs ${getMutedText(isDarkMode)}`}>
                      Scheduled for {formatDateOnly(item.date)}
                    </p>
                  </div>
                  <span className={`font-semibold ${getPrimaryText(isDarkMode)}`}>
                    ${item.amount.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            {/* Total Summary */}
            <div
              className={`mt-4 pt-3 border-t flex justify-between ${
                isDarkMode ? "border-gray-600" : "border-gray-300"
              }`}
            >
              <span className={`font-semibold ${getPrimaryText(isDarkMode)}`}>
                Total Amount
              </span>
              <span className={`font-bold text-lg ${getPrimaryText(isDarkMode)}`}>
                ${invoiceAmount.toFixed(2)}
              </span>
            </div>
            <p className={`text-xs mt-2 ${getMutedText(isDarkMode)}`}>
              {installments} payments of ${installmentAmount.toFixed(2)} each
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPlanCreator;
